import React from 'react';

import { DataTable, type TableColumn, SimpleButton } from '@/src/ui';
import type { ExamQuestionListItemDTO } from '../models/ExamDTOs';
import { ReportCellStyle, ReportHeaderStyle } from '@/src/utils';

interface QuestionTableProps {
    data: ExamQuestionListItemDTO[];
    onAction: (
        q: ExamQuestionListItemDTO,
        action: 'add' | 'remove' | 'moveUp' | 'moveDown'
    ) => void;
    actionType: 'add' | 'remove';
    allowReorder?: boolean;
}

const QuestionTable: React.FC<QuestionTableProps> = ({
    data,
    onAction,
    actionType,
    allowReorder,
}) => {
    const columns: TableColumn[] = [
        {
            dataKey: 'title',
            header: 'Title',
            align: 'left',
            allowSort: actionType === 'add',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'actions',
            header: 'Actions',
            align: 'left',
            emptyValue: '-',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row: ExamQuestionListItemDTO, index?: number) => (
                <div style={{ display: 'flex', gap: 8 }}>
                    {actionType === 'add' && (
                        <SimpleButton onClick={() => onAction(row, 'add')}>Add</SimpleButton>
                    )}
                    {actionType === 'remove' && (
                        <>
                            <SimpleButton color="secondary" onClick={() => onAction(row, 'remove')}>
                                Remove
                            </SimpleButton>
                            {allowReorder && (
                                <>
                                    <SimpleButton
                                        onClick={() => onAction(row, 'moveUp')}
                                        disabled={index === 0}
                                    >
                                        ↑
                                    </SimpleButton>
                                    <SimpleButton
                                        onClick={() => onAction(row, 'moveDown')}
                                        disabled={index === data.length - 1}
                                    >
                                        ↓
                                    </SimpleButton>
                                </>
                            )}
                        </>
                    )}
                </div>
            ),
        },
    ];

    return (
        <DataTable
            data={data}
            columns={columns}
            tableName={actionType === 'add' ? 'Available Questions' : 'Selected Questions'}
            defaultSortColumn={actionType === 'add' ? 'title' : ''}
            rowsPerPageInitial={10}
            alternatingRowColor
            defaultSortDirection="asc"
            emptyMessage={
                actionType === 'add'
                    ? 'No questions available. Click Search to load questions.'
                    : 'No questions selected.'
            }
        />
    );
};

export default QuestionTable;
